<script setup lang="ts">
import { computed } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore, ZOOM_STEPS } from '@/stores/uiStore'
import * as actions from '@/app/actions'
import { bridgeEnabled, bridgeState, bridgeCalls } from '@/mcp/bridgeClient'
import { pixelStore } from '@/pixels/pixelStore'
import { useI18n } from '@/i18n'
import { aiProgress } from '@/ai/runtime'

const docStore = useDocumentStore()
const ui = useUiStore()
const { t } = useI18n()
const appVersion = __APP_VERSION__

const memory = computed(() => {
  void docStore.historyVersion
  const mb = pixelStore.byteLength() / 1048576
  return mb >= 100 ? `${mb.toFixed(0)} MB` : `${mb.toFixed(1)} MB`
})
const zoomText = computed(() => `${(ui.view.zoom * 100).toFixed(ui.view.zoom < 0.1 ? 2 : 1)}%`)
function setZoomFromSelect(e: Event) {
  ui.setZoomAt(Number((e.target as HTMLSelectElement).value), { x: ui.viewportSize.w / 2, y: ui.viewportSize.h / 2 })
}
const cursorText = computed(() => (ui.cursorDocPos ? `${Math.floor(ui.cursorDocPos.x)}, ${Math.floor(ui.cursorDocPos.y)}` : ''))
</script>

<template>
  <div class="status mono">
    <span v-if="aiProgress" class="ai">{{ t('ai.progress', { label: aiProgress.label, pct: Math.round(aiProgress.ratio * 100) }) }}</span>
    <template v-if="docStore.doc">
      <span class="zoom row">
        <button class="ghost tiny" @click="ui.zoomStep(-1)">−</button>
        <select class="zoomsel" :value="ui.view.zoom" @change="setZoomFromSelect">
          <option v-for="z in ZOOM_STEPS" :key="z" :value="z">{{ Math.round(z * 1000) / 10 }}%</option>
          <option v-if="!ZOOM_STEPS.includes(ui.view.zoom)" :value="ui.view.zoom">{{ zoomText }}</option>
        </select>
        <button class="ghost tiny" @click="ui.zoomStep(1)">＋</button>
        <button class="ghost tiny" :title="t('menu.view.fit')" @click="actions.zoomFit()">⤢</button>
        <button class="ghost tiny" :title="t('menu.view.actual')" @click="actions.zoomActual()">1:1</button>
      </span>
      <span>{{ t('status.size') }} {{ docStore.doc.meta.width }}×{{ docStore.doc.meta.height }} px</span>
      <span>{{ t('status.memory') }} {{ memory }}</span>
      <span class="cursor">{{ cursorText }}</span>
    </template>
    <span v-else class="muted">{{ t('status.noDocument') }}</span>
    <span class="spacer" />
    <span v-if="bridgeEnabled" class="bridge" :class="bridgeState" :title="t(`bridge.${bridgeState}` as any)">● {{ t(`bridge.${bridgeState}` as any) }}<template v-if="bridgeCalls"> ({{ bridgeCalls }})</template></span>
    <span class="muted">v{{ appVersion }}</span>
  </div>
</template>

<style scoped>
.status {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 0 10px;
  font-size: 11px;
  background: var(--bg-1);
  color: var(--fg-1);
}
.cursor {
  min-width: 90px;
}
.zoom {
  gap: 2px;
}
.zoomsel {
  height: 18px;
  padding: 0 4px;
  font-size: 11px;
  width: 78px;
}
.bridge {
  font-size: 11px;
  color: var(--fg-2);
}
.bridge.connected {
  color: #4caf50;
}
.bridge.connecting {
  color: #e8a838;
}
.tiny {
  height: 18px;
  padding: 0 5px;
  font-size: 11px;
}
.ai {
  color: var(--accent);
  margin-right: 8px;
}
</style>
